import React, { createContext, useContext, useState, useEffect } from 'react';

export type Theme = 'dark' | 'light' | 'professional' | 'hybrid' | 'sharp' | 'minimalist' | 'minimalist-dark' | 'pastel';

export interface ThemeDefinition {
    id: Theme;
    name: string;
    description: string;
    // We can store specific visual properties here if we want to render the preview dynamically, 
    // but for now we'll stick to the ID being the key.
}

interface ThemeContextType {
    theme: Theme;
    toggleTheme: () => void;
    setTheme: (theme: Theme) => void;
    availableThemes: ThemeDefinition[];
    deleteTheme: (id: Theme) => void;
    defaultTheme: Theme;
    setDefaultTheme: (id: Theme) => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

const INITIAL_THEMES: ThemeDefinition[] = [
    { id: 'pastel', name: 'Pastel (L\'Ardoise)', description: 'Tons chauds, organiques et doux (Par défaut)' },
    { id: 'dark', name: 'Sombre (Glass)', description: 'Sombre futuriste' },
    { id: 'light', name: 'Clair (Glass)', description: 'Lumineux et aéré' },
    { id: 'professional', name: 'Professionnel', description: 'Flat design, contrasté' },
    { id: 'hybrid', name: 'Hybride (Next-Gen)', description: 'UI Sombre / Contenu Clair' },
    { id: 'sharp', name: 'Sharp (Flat)', description: 'Carré, Précis, Monochrome' },
    { id: 'minimalist', name: 'Minimalist', description: 'Slate, Épuré, Premium' },
    { id: 'minimalist-dark', name: 'Minimalist Dark', description: 'Slate Dark, Épuré' },
];

export const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    // 1. Manage Default Theme
    const [defaultTheme, setDefaultThemeState] = useState<Theme>(() => {
        return (localStorage.getItem('system_default_theme') as Theme) || 'pastel';
    });

    // 2. Manage Available Themes
    const [availableThemes, setAvailableThemes] = useState<ThemeDefinition[]>(() => {
        const saved = localStorage.getItem('available_themes');
        return saved ? JSON.parse(saved) : INITIAL_THEMES;
    });

    // 3. Manage Current Theme
    const [theme, setThemeState] = useState<Theme>(() => {
        const savedTheme = localStorage.getItem('theme') as Theme;
        if (savedTheme && availableThemes.some(t => t.id === savedTheme)) return savedTheme;
        return defaultTheme;
    });

    useEffect(() => {
        document.documentElement.setAttribute('data-theme', theme);
        localStorage.setItem('theme', theme);
    }, [theme]);

    useEffect(() => {
        localStorage.setItem('system_default_theme', defaultTheme);
    }, [defaultTheme]);

    useEffect(() => {
        localStorage.setItem('available_themes', JSON.stringify(availableThemes));
    }, [availableThemes]);

    const toggleTheme = () => {
        setThemeState(prev => prev === 'dark' ? 'light' : 'dark');
    };

    const setTheme = (newTheme: Theme) => {
        setThemeState(newTheme);
    };

    const setDefaultTheme = (newDefault: Theme) => {
        setDefaultThemeState(newDefault);
    };

    const deleteTheme = (id: Theme) => {
        if (availableThemes.length <= 1) {
            alert("Il faut au moins un thème actif.");
            return;
        }
        if (id === theme) {
            alert("Vous ne pouvez pas supprimer le thème actif.");
            return;
        }

        setAvailableThemes(prev => prev.filter(t => t.id !== id));
    };

    return (
        <ThemeContext.Provider value={{
            theme,
            toggleTheme,
            setTheme,
            availableThemes,
            deleteTheme,
            defaultTheme,
            setDefaultTheme
        }}>
            {children}
        </ThemeContext.Provider>
    );
};

// eslint-disable-next-line react-refresh/only-export-components
export const useTheme = () => {
    const context = useContext(ThemeContext);
    if (context === undefined) {
        throw new Error('useTheme must be used within a ThemeProvider');
    }
    return context;
};
